import {
  cloneElement,
  isValidElement,
  PointerEvent,
  useCallback,
  useState,
} from "react";
import { Tooltip as TooltipMantine, TooltipProps } from "@mantine/core";
import { useShownColorScheme } from "hooks/useShownColorScheme";
import { TOOLTIP_SHOW_TIMEOUT } from "config/constants";

type ChildProps = {
  onPointerEnter?: (event: PointerEvent<HTMLElement>) => void;
};

/**
 * A Mantine tooltip that's created when the pointer first gets to its element.
 *
 * A Mantine tooltip updates its state while React commits it (it registers its element and creates its portal and id),
 * so React rerenders it synchronously, together with everything committed with it - the toolbars have many tooltips,
 * which made the app's startup a few long tasks. Until it's needed, the element is rendered alone. The tooltip is then
 * rendered next to it (for the element, with `target`), so the element isn't recreated: it opens when the pointer
 * moves over the element, after the usual delay.
 */
export const LazyTooltip = ({ children, ...rest }: TooltipProps) => {
  const [target, setTarget] = useState<HTMLElement | null>(null);

  const child = isValidElement<ChildProps>(children) ? children : null;
  const childOnPointerEnter = child?.props.onPointerEnter;
  const onPointerEnter = useCallback(
    (event: PointerEvent<HTMLElement>) => {
      childOnPointerEnter?.(event);
      // (also when the element was replaced)
      setTarget(event.currentTarget);
    },
    [childOnPointerEnter]
  );

  if (!child) return children;

  return (
    <>
      {cloneElement(child, { onPointerEnter })}
      {target ? <TooltipMantine {...rest} target={target} /> : null}
    </>
  );
};

/** The app's tooltip (a LazyTooltip) */
export const Tooltip = ({ children, ...rest }: TooltipProps) => {
  const colorScheme = useShownColorScheme();

  return (
    <LazyTooltip
      openDelay={TOOLTIP_SHOW_TIMEOUT}
      {...rest}
      withArrow
      withinPortal
      color={colorScheme === "dark" ? "gray" : "dark"}
    >
      {children}
    </LazyTooltip>
  );
};
